/**
 * Navbar and user menu
 */
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  AppBar,
  Grid,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ExpandMore as ExpandMoreIcon,
  AccountCircle,
  Logout as LogoutIcon,
  Person as ProfileIcon,
  Email as EmailIcon,
  MonitorHeart as DashboardIcon,
} from "@mui/icons-material";
import TopHeader from "./TopHeader";
import { logout } from "@smfa/utils/src";
import { persistor } from "../../store";

/**
 * User Login/Options menu
 * @returns {JSX}
 * @component
 */
const UserMenu = ({ isMobile = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth) || {};
  const user = useSelector((state) => state.user.data) || {};
  const isAuthenticated = Boolean(auth?.isAuthenticated);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleUserMenuOpen = (event) => setAnchorEl(event.currentTarget);

  const handleUserMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    logout(dispatch, { persistor });
    handleUserMenuClose();
    // Force reload to ensure all state is cleared
    window.location.href = "/";
  };

  const handleDashboardClick = () => {
    handleUserMenuClose();
    navigate("/dashboard");
  };

  const handleProfileClick = () => {
    handleUserMenuClose();
    navigate("/profile");
  };

  useEffect(() => {
    // Log debugging info
    log.debug("NavbarFive: UserMenu() auth state:", {
      auth,
      isAuthenticated,
      user,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Not logged in - show login button
  if (!isAuthenticated || !user) {
    return (
      <Box
        sx={
          isMobile
            ? { mt: 2, textAlign: "center" }
            : { display: { xs: "none", md: "flex" } }
        }
      >
        <Button
          component={Link}
          to="/login"
          variant="contained"
          color="primary"
          sx={{
            textTransform: !isMobile ? "none" : "default",
            borderRadius: "4px",
            padding: "10px 20px",
            color: "white",
            "&:hover": {
              color: "white",
            },
          }}
        >
          Member Login
        </Button>
      </Box>
    );
  }

  // Logged in - show user menu
  // Safely access display name with fallbacks
  let _displayName = "Member";
  if (user && user.profile) {
    if (user.profile._displayName) {
      _displayName = user.profile._displayName;
    } else if (user.profile.first_name && user.profile.last_name) {
      const title = user.profile.title ? `${user.profile.title} ` : "";
      _displayName = `${title}${user.profile.first_name} ${user.profile.last_name}`;
    }
  }

  // User menu content for mobile view
  const mobileMenuContent = (
    <Box sx={{ mt: 2 }}>
      <List disablePadding>
        <ListItem disablePadding>
          <ListItemButton onClick={handleDashboardClick}>
            <Tooltip placement="left" title="Go to my dashboard">
              <Box sx={{ px: 0, py: 0 }}>
                <Typography variant="body1" fontWeight="bold">
                  <DashboardIcon sx={{ mr: 1 }} />
                  {_displayName}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 1,
                    opacity: 0.7,
                  }}
                >
                  <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {user.email || "No email provided"}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1 }} />

        <ListItem disablePadding>
          <ListItemButton onClick={handleProfileClick}>
            <ProfileIcon sx={{ mr: 1 }} />
            <ListItemText primary="Profile" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <LogoutIcon sx={{ mr: 1 }} />
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  // Render based on mobile/desktop
  if (isMobile) {
    // Mobile view - render directly in drawer
    return mobileMenuContent;
  }

  // Desktop view - render icon button with dropdown
  return (
    <Box sx={{ display: { xs: "none", md: "flex" } }}>
      <IconButton
        onClick={handleUserMenuOpen}
        size="large"
        edge="end"
        aria-label="account of current user"
        aria-haspopup="true"
        color="inherit"
      >
        <AccountCircle />
      </IconButton>
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
      >
        {/* Don't use fragment as direct child of Menu */}
        <MenuItem onClick={handleDashboardClick}>
          <Tooltip title="Go to my dashboard">
            <Box sx={{ minWidth: 200 }}>
              <DashboardIcon sx={{ mr: 1 }} />
              {_displayName}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 1,
                  opacity: 0.7,
                }}
              >
                <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {user.email || "No email provided"}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleProfileClick}>
          <ProfileIcon fontSize="small" sx={{ mr: 1 }} />
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

/**
 * Navbar
 * @returns {JSX}
 * @component
 */
const NavbarFive = () => {
  const user = useSelector((state) => state.user);
  const isSignedUp = Boolean(user?.data?.id && user?.data?.id > 0);
  //const isMembershipActive = get(user, "data.isMembershipActive", false);

  const { home: homeData } = useSelector((state) => state.pageContents);
  const [currentPath, setCurrentPath] = useState("");
  // Dropdown menu states
  const [anchorElMembership, setAnchorElMembership] = useState(null);
  const [anchorElResources, setAnchorElResources] = useState(null);
  // Mobile drawer menu expansion states
  const [openMembershipMobile, setOpenMembershipMobile] = useState(false);
  const [openResourcesMobile, setOpenResourcesMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Menu handlers
  const handleOpenMembershipMenu = (event) =>
    setAnchorElMembership(event.currentTarget);

  const handleOpenResourcesMenu = (event) =>
    setAnchorElResources(event.currentTarget);

  const handleCloseMenu = () => {
    setAnchorElMembership(null);
    setAnchorElResources(null);
  };

  // Navigation items
  const pages = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about-us" },
    // { name: "Affiliates", path: "/affiliates" },
    {
      name: "Membership",
      hasDropdown: true,
      handler: handleOpenMembershipMenu,
      children: [
        { name: "Benefits of Membership", path: "/benefitsmembership" },
        { name: "Membership Categories", path: "/membershipcategories" },
        !isSignedUp ? { name: "Sign Up", path: "/signup" } : false,
        { name: "Get Involved", path: "/getinvolved" },
      ].filter(Boolean),
    },
    { name: "Meetings", path: "/meeting" },
    {
      name: "Resources",
      hasDropdown: true,
      handler: handleOpenResourcesMenu,
      children: [
        { name: "Fiesta Sessions", path: "/fiesta-sessions" },
        { name: "Member Directory", path: "/member-directory" },
        // { name: "Guidelines", path: "/guidelines" },
      ],
    },
    //{ name: "Contact Us", path: "/contact" },
  ];

  // Mobile drawer toggle
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Track window location
  useEffect(() => {
    setCurrentPath(window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  // Handle sticky header
  useEffect(() => {
    const handleScroll = () => {
      const navbarElement = document.getElementById("navbar");
      if (navbarElement) {
        if (window.scrollY > 170) {
          navbarElement.classList.add("is-sticky");
        } else {
          navbarElement.classList.remove("is-sticky");
        }
      }
    };

    document.addEventListener("scroll", handleScroll);
    return () => document.removeEventListener("scroll", handleScroll);
  }, []);

  // Mobile drawer content
  const drawer = (
    <Box sx={{ width: 280, p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        {homeData?.widgetdata && (
          <img
            src={homeData.widgetdata.header_logo}
            alt="logo"
            style={{ maxWidth: "80%", height: "auto" }}
          />
        )}
      </Box>
      <List>
        {pages.map((page) => (
          <React.Fragment key={page.name}>
            {page.hasDropdown ? (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (page.name === "Membership")
                        setOpenMembershipMobile(!openMembershipMobile);
                      if (page.name === "Resources")
                        setOpenResourcesMobile(!openResourcesMobile);
                    }}
                    sx={{ px: 2, py: 1 }}
                  >
                    <ListItemText primary={page.name} />
                    <ExpandMoreIcon />
                  </ListItemButton>
                </ListItem>
                <Collapse
                  in={
                    page.name === "Membership"
                      ? openMembershipMobile
                      : openResourcesMobile
                  }
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {page.children.map((child) => (
                      <ListItem key={child.name} disablePadding>
                        <ListItemButton
                          component={Link}
                          to={child.path}
                          onClick={handleDrawerToggle}
                          sx={{ pl: 4 }}
                        >
                          <ListItemText primary={child.name} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to={page.path}
                  onClick={handleDrawerToggle}
                  sx={{ px: 2, py: 1 }}
                >
                  <ListItemText primary={page.name} />
                </ListItemButton>
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>
      <UserMenu isMobile={true} />
    </Box>
  );

  return (
    <header className="header-area fixed-top">
      <TopHeader />
      <div className="nav-area five">
        <div id="navbar" className="navbar-area">
          <Box
            sx={{
              flexGrow: 1,
              borderTop: "1px dotted #b1b9ba",
              borderBottom: "1px dotted #b1b9ba",
            }}
          >
            <AppBar
              position="static"
              color="transparent"
              elevation={0}
              sx={{
                backgroundColor: "#eff7f8",
              }}
            >
              <Container maxWidth={false}>
                <Toolbar disableGutters>
                  {/* Logo */}
                  <Box
                    component={Link}
                    to="/"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      textDecoration: "none",
                      color: "inherit",
                      mr: 2,
                    }}
                  >
                    {homeData?.widgetdata && (
                      <img
                        src={homeData.widgetdata.header_logo}
                        alt="logo"
                        style={{ maxHeight: "50px" }}
                      />
                    )}
                  </Box>

                  {/* Mobile menu button */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: { xs: "flex", md: "none" },
                      justifyContent: "flex-end",
                    }}
                  >
                    <IconButton
                      size="large"
                      aria-label="menu"
                      aria-controls="menu-appbar"
                      aria-haspopup="true"
                      onClick={handleDrawerToggle}
                      color="inherit"
                    >
                      <MenuIcon />
                    </IconButton>
                  </Box>

                  {/* Desktop navigation */}
                  <Grid
                    container
                    justifyContent="center"
                    sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        "& .MuiButton-root": {
                          color: "black",
                          textTransform: "none",
                          fontSize: "16px",
                          fontWeight: 500,
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: "primary.main",
                          },
                        },
                      }}
                    >
                      {pages.map((page) =>
                        page.hasDropdown ? (
                          <Box key={page.name}>
                            <Button
                              onClick={page.handler}
                              endIcon={<ExpandMoreIcon />}
                              sx={{
                                color:
                                  currentPath === page.path
                                    ? "primary.main"
                                    : "inherit",
                              }}
                            >
                              {page.name}
                            </Button>
                            <Menu
                              anchorEl={
                                page.name === "Membership"
                                  ? anchorElMembership
                                  : anchorElResources
                              }
                              open={
                                page.name === "Membership"
                                  ? Boolean(anchorElMembership)
                                  : Boolean(anchorElResources)
                              }
                              onClose={handleCloseMenu}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "center",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "center",
                              }}
                              sx={{ mt: "1px" }}
                            >
                              {page.children.map((child) => (
                                <MenuItem
                                  key={child.name}
                                  onClick={handleCloseMenu}
                                  component={Link}
                                  to={child.path}
                                >
                                  <Typography textAlign="center">
                                    {child.name}
                                  </Typography>
                                </MenuItem>
                              ))}
                            </Menu>
                          </Box>
                        ) : (
                          <Button
                            key={page.name}
                            component={Link}
                            to={page.path}
                            sx={{
                              color:
                                currentPath === page.path
                                  ? "primary.main"
                                  : "inherit",
                            }}
                          >
                            {page.name}
                          </Button>
                        ),
                      )}
                    </Stack>
                  </Grid>

                  {/* Member login button */}
                  <UserMenu isMobile={false} />
                </Toolbar>
              </Container>
            </AppBar>
          </Box>

          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            anchor="right"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better mobile performance
            }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": { boxSizing: "border-box", width: 280 },
            }}
          >
            {drawer}
          </Drawer>
        </div>
      </div>
    </header>
  );
};

export default NavbarFive;
